import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { FolderSync, Table2, BellRing, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader } from "./ui/card";
import { Button } from "./ui/button";

const FeaturesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    {
      icon: FolderSync,
      title: "Zero-Touch Filing",
      highlight: "Instant Organization",
      description:
        "Your client sends a photo. It lands in your specific server folder, instantly renamed as ClientName_Invoice_Date.pdf. You never touch the phone.",
      benefit: "Eliminates hours of manual file sorting",
    },
    {
      icon: Table2,
      title: "The Master Inward Register",
      highlight: "Perfect Audit Trail",
      description:
        "Every document is automatically logged in a Master Sheet with a timestamp. Create a perfect digital audit trail without typing a single row.",
      benefit: "Never miss a document again",
    },
    {
      icon: BellRing,
      title: "Automated Acknowledgment",
      highlight: "Professional Replies",
      description:
        'Your client gets an instant "Received & Filed" reply. Stop wasting time confirming receipts. Look professional, automatically.',
      benefit: "End the back-and-forth forever",
    },
  ];

  const scrollToDemo = (e) => {
    e.preventDefault();
    const element = document.querySelector("#book-demo");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  return (
    <section
      id="features"
      ref={ref}
      className="py-20 md:py-32 bg-cream relative"
      data-testid="features-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <p className="font-body text-sm font-semibold uppercase tracking-widest text-gold mb-4">
            The Solution
          </p>
          <h2
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight tracking-tight mb-6"
            data-testid="features-headline"
          >
            Your New "Invisible" Workflow
          </h2>
          <p className="font-body text-base md:text-lg text-slate-muted max-w-2xl mx-auto">
            An invisible system that works entirely in the background. 
            Your clients keep using WhatsApp. Your documents get organized. 
            You get peace of mind.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid md:grid-cols-3 gap-8"
        >
          {features.map((feature, index) => (
            <motion.div key={index} variants={itemVariants}>
              <Card
                className="premium-card h-full bg-white border-black/5 hover:border-gold/30 group"
                data-testid={`feature-card-${index}`}
              >
                <CardHeader className="pb-4">
                  <div className="w-14 h-14 bg-navy/5 rounded-xl flex items-center justify-center mb-4 group-hover:bg-navy/10 transition-colors duration-300">
                    <feature.icon size={28} className="text-navy" />
                  </div>
                  <p className="font-body text-xs font-semibold uppercase tracking-widest text-gold mb-2">
                    {feature.highlight}
                  </p>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-navy">
                    {feature.title}
                  </h3>
                </CardHeader>
                <CardContent>
                  <p className="font-body text-slate-muted leading-relaxed mb-4">
                    {feature.description}
                  </p>
                  <div className="pt-4 border-t border-black/5">
                    <p className="font-body text-sm font-semibold text-navy flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-gold rounded-full" />
                      {feature.benefit}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <Button
            onClick={scrollToDemo}
            className="cta-primary inline-flex items-center gap-2"
            data-testid="features-cta-button"
          >
            See It In Action
            <ArrowRight size={18} />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
